import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Moon, Sun, LogOut, User, BookOpen, Lightbulb, Home, Info, Terminal, Focus, Menu, X } from 'lucide-react';
import { useTheme } from './theme-provider';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function Navigation({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/dashboard', icon: Home, label: 'Início' },
    { path: '/about', icon: Info, label: 'Sobre' },
    { path: '/modules', icon: BookOpen, label: 'Módulos' },
    { path: '/ai-tools', icon: Lightbulb, label: 'Ferramentas' },
    { path: '/plano-ai-first', icon: Focus, label: 'Plano AI First' },
    { path: '/profile', icon: User, label: 'Perfil' },
  ];

  if (user?.role === 'admin' || user?.role === 'super_admin') {
    navItems.push({ path: '/admin', icon: Terminal, label: 'Painel Admin' });
  }

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('authToken');
      delete axios.defaults.headers.common['Authorization'];
      toast.success('Session Terminated');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('authToken');
      delete axios.defaults.headers.common['Authorization'];
      navigate('/');
    }
  };

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-white/10 bg-[#0B0E14]/95 backdrop-blur-sm">
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/dashboard" className="flex items-center gap-3 group" data-testid="nav-logo">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-primary flex items-center justify-center font-bold text-background leading-none text-sm">
                  AI
                </div>
                <div className="flex flex-col ml-3 justify-center">
                  <span className="text-xl font-bold tracking-tighter font-heading text-foreground group-hover:text-primary transition-colors leading-none">
                    AIFT_OS
                  </span>
                  <span className="text-[9px] font-mono opacity-40 uppercase tracking-[0.2em] leading-none mt-1">Plataforma v2.0</span>
                </div>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-link-${item.label.toLowerCase().replace(/ /g, '-')}`}
                    className={`flex items-center gap-2 px-4 py-2 text-xs font-mono tracking-wide uppercase transition-all border border-transparent ${isActive
                      ? 'text-aift-orange bg-aift-orange/10 border-aift-orange/20'
                      : 'text-muted-foreground hover:text-white hover:border-white/10'
                      }`}
                  >
                    <Icon className="h-3 w-3" strokeWidth={2} />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Theme Toggle Hidden - Dark Mode Enforced in Master Design */}

            {user && (
              <>
                <div className="hidden md:flex items-center gap-4 pl-4 border-l border-white/10">
                  <div className="flex flex-col items-end">
                    <span className="text-xs font-bold font-mono text-white uppercase">{user.name}</span>
                    <span className="text-[10px] font-mono text-aift-green">ONLINE</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLogout}
                    data-testid="logout-button"
                    className="rounded-none border-white/20 bg-transparent text-white hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/50 font-mono text-xs uppercase transition-all"
                  >
                    <LogOut className="h-3 w-3 mr-2" />
                    Sair
                  </Button>
                </div>
                
                {/* Mobile Menu Toggle */}
                <button 
                  className="md:hidden p-2 text-foreground hover:text-primary transition-colors"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                  {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && user && (
        <div className="md:hidden border-t border-white/10 bg-[#0B0E14] px-4 py-4 space-y-2 shadow-2xl">
          <div className="flex flex-col items-start gap-1 mb-4 pb-4 border-b border-white/10">
            <span className="text-xs font-bold font-mono text-white uppercase">{user.name}</span>
            <span className="text-[10px] font-mono text-aift-green">ONLINE</span>
          </div>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 text-sm font-mono tracking-wide uppercase transition-all rounded-lg ${isActive
                  ? 'text-aift-orange bg-aift-orange/10 border border-aift-orange/20'
                  : 'text-muted-foreground hover:text-white hover:bg-white/5'
                  }`}
              >
                <Icon className="h-4 w-4" strokeWidth={2} />
                {item.label}
              </Link>
            );
          })}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setIsMobileMenuOpen(false);
              handleLogout();
            }}
            className="w-full mt-4 justify-start rounded-lg border-white/20 bg-transparent text-white hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/50 font-mono text-xs uppercase"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sair
          </Button>
        </div>
      )}
    </nav>
  );
}